import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import { getEssay } from '../../data/essays';
import { ArrowLeft } from 'lucide-react';
import PullQuote from '../../components/essays/PullQuote';

const essay = getEssay('the-second-loop')!;

const TheSecondLoop = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": essay.title,
    "description": essay.subtitle,
    "datePublished": essay.date,
    "dateModified": essay.dateModified ?? essay.date,
    "author": {
      "@type": "Person",
      "name": essay.author,
      "url": "https://www.linkedin.com/in/kent-frazier/",
    },
    "publisher": {
      "@type": "Organization",
      "name": "ParadoxEdge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.paradoxedge.com/lovable-uploads/180210a9-9a73-4038-a36b-f373598ef48a.png",
      },
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.paradoxedge.com/essays/${essay.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${essay.title} | Essays`}
        description={essay.subtitle}
        canonicalUrl={`/essays/${essay.slug}`}
        ogType="article"
        publishedTime={essay.date}
        modifiedTime={essay.dateModified ?? essay.date}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        <main className="flex-1">
          <article className="py-20 sm:py-28">
            <div className="container-mobile max-w-[42rem]">
              {/* Back link */}
              <Link
                to="/essays"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 text-sm text-foreground/90 hover:text-primary transition-colors mb-10"
              >
                <ArrowLeft className="w-4 h-4" />
                All essays
              </Link>

              {/* Header */}
              <header className="mb-12 sm:mb-16">
                <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-primary font-semibold mb-6">
                  Essay
                </p>
                <h1 className="mb-6">{essay.title}</h1>
                <p className="font-display text-xl sm:text-2xl text-foreground/95 leading-snug mb-8 italic">
                  {essay.subtitle}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground border-t border-border pt-6">
                  <span className="font-semibold text-foreground">By {essay.author}</span>
                  <span aria-hidden="true">·</span>
                  <time dateTime={essay.date}>{essay.dateDisplay}</time>
                  <span aria-hidden="true">·</span>
                  <span>{essay.readingMinutes} min read</span>
                  {essay.dateModified && essay.dateModified !== essay.date && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>
                        Updated{' '}
                        <time dateTime={essay.dateModified}>{essay.dateModifiedDisplay}</time>
                      </span>
                    </>
                  )}
                </div>
              </header>

              {/* Body */}
              <div className="essay-prose">
                {essay.summary && (
                  <aside
                    aria-label="In short"
                    className="not-prose mb-10 rounded-sm border-l-2 border-primary bg-secondary/40 px-6 py-5"
                  >
                    <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-2">
                      In short
                    </p>
                    <p className="text-foreground/85 leading-relaxed">
                      {essay.summary}
                    </p>
                  </aside>
                )}
                <p>
                  Reid Hoffman recently published a conversation with Satya Nadella that every CEO will be quoting within the year. The subject is what they call token capital: the idea that your organization's tacit knowledge, the judgment and taste and million microdecisions living in your people, can now be extracted and encoded into model weights that you own, control, and compound.
                </p>
                <p>
                  Satya's test for CEOs is admirably concrete. Can you stand up and say: based on everything that happened across our operations last night, here is the knowledge we converted into token capital, that we own, that we control, and that we've built a system to compound? If you can't answer that, he argues, you don't have an AI strategy. You have an AI announcement.
                </p>
                <p>
                  I take this argument seriously, and I want to show you the sentence buried inside it that undoes the whole frame and offer a companion test, one I believe will separate the organizations that thrive through this transition from the ones that quietly hollow themselves out while their dashboards glow green.
                </p>

                <h2>Tacit knowledge is mortal</h2>
                <p>
                  This is the truth at the center of Nadella's case, and it has haunted organizations forever. The master machinist's feel for the lathe, the underwriter's instinct for the loan that looks fine on paper and isn't, the senior engineer's sense of which shortcut will hold and which will collapse in production: all of it walks out the door with every retirement, every resignation, every reorg. Nobody mourns it because it was never counted. For all of human history, a primary way to carry judgment forward was apprenticeship.
                </p>
                <p>
                  Models change that calculus. Encoding judgment is an act of preservation. The junior analyst in Jakarta gains access to discernment that used to live behind a closed door in Boston. There is something almost reverent in this, if you squint: a technology that refuses to let wisdom die with its holder.
                </p>
                <p>
                  The ownership argument is equally serious. If you don't train loops on your own data, inside systems you control, you will rent your organization's intelligence from a vendor who is simultaneously learning your business. Nadella is telling CEOs to hold their own ground.
                </p>
                <p>
                  Even the capital language, cold as it sounds, has a defense. Things named as capital get funded. Training budgets exist because someone once convinced a CFO that people were an investment. Calling tokens capital imposes discipline: measurement, accountability, compounding.
                </p>
                <p>
                  Let me be clear about what I am about to offer. The problem with the token capital thesis is what it leaves out, and what it leaves out happens to be the thing Nadella himself identifies as most scarce.
                </p>

                <h2>The scarce resources</h2>
                <p>
                  Buried in Hoffman's post is this: in an age of token abundance, the scarce resources are human taste, human judgment, and the capacity to direct all that capability toward outcomes that actually matter.
                </p>
                <p>
                  Read it again. Intelligence is abundant. Tokens are abundant. The generation of text and code and analysis and imagery is approaching free. What remains scarce, by Nadella's own account, is the human capacity to know what's worth doing with any of it.
                </p>
                <PullQuote>
                  The scarce resource receives an annual performance review. The abundant one gets a compounding engine running every night.
                </PullQuote>
                <p>
                  He named the scarce asset. Then every prescription that followed was for the abundant one.
                </p>
                <p>
                  This is the strangest feature of the entire token capital conversation, and once you see it, you cannot unsee it. The strategy says: build a nightly loop to capture, encode, and compound your tokens. The strategy says nothing about how taste deepens, how judgment matures, how the capacity to discern what matters actually grows in a human being. The scarce resource receives an annual performance review. The abundant one gets a compounding engine running every night.
                </p>

                <h2>What the organization owns</h2>
                <p>
                  Before we get to the human question, the ownership claim itself deserves scrutiny, because it's weaker than it sounds.
                </p>
                <p>
                  Model weights are a lossy derivative of how your people work. Try the audit. Can you trace a learned behavior back to its source? Can you verify what was retained and what was distorted in the encoding? Can you transfer the knowledge to another model, query it without the original vendor, prove what the system knew at a specific moment, remove the platform without losing the institutional memory? For most organizations, the honest answer down the entire list is no. What they own is a snapshot they cannot inspect, executable only inside someone else's stack.
                </p>
                <p>
                  And here is the deeper irony. The classic critique of the phrase human capital was always that companies don't own it. People can leave. The knowledge walks. Token capital arrives wearing the costume of a solution and carries the identical flaw, with a vendor now holding the door.
                </p>
                <p>
                  A behavior you cannot trace to its source is a behavior no one can answer for.
                </p>

                <h2>The honest concession, and the deeper ground</h2>
                <p>
                  There is a romantic version of the humanist response to all this, and I want to steer clear of it, because it collapses against reality. The romantic version says machines merely remix while humans truly create. Anyone paying attention knows this is no longer true. Models fold proteins no one has folded, write proofs no one has written, produce novelty at almost no marginal cost. If your case for human worth rests on a monopoly on newness, your case is already lost.
                </p>
                <p>
                  The ground that holds is different, and it is harder.
                </p>
                <p>
                  Accountability has exactly one source. When a system harms a patient, breaks a promise, or sinks a deal, the sentence "the model decided" will satisfy no court, no board, no grieving family. A human answers. This is structural, written into law and contract and conscience, and it does not weaken as models improve. It intensifies. The more capability flows through the system, the more weight lands on the person who must stand behind what it does.
                </p>
                <p>
                  And the capacities that make a human worth standing behind anything are precisely the ones on Nadella's scarcity list, plus the ones he didn't name. Integrity, which is wholeness under pressure. Ethical depth, which is the capacity to choose against your own advantage when it matters. The relational depth that makes trust possible at all, because trust exists only between beings who could let each other down and choose otherwise. A model can be reliable. Only a person can be trustworthy. Entire economies run on the second.
                </p>
                <p>
                  Here is what over 30 years inside organizations has taught me about these capacities: nobody is born with them, no company acquires them by hiring, and no model weights contain them. They are developmental achievements. They grow through practice, through relationships, through the slow ripening of people who are developing rather than merely performing. And they atrophy the same way they grow: quietly, cumulatively, while everyone watches a different dashboard.
                </p>

                <h2>The companion test</h2>
                <p>
                  Here is the test I would set beside Satya's, offered in the same spirit and with the same demand for concreteness.
                </p>
                <p>
                  Can you stand up and say: based on everything that happened across our operations last night, here is the judgment our people deepened, the taste they sharpened, the trust they built, and here is the system we run to compound it?
                </p>
                <p>
                  Most CEOs cannot. Most companies are now building a nightly loop for their tokens while running an annual review for their humans. They are constructing a compounding engine for the abundant asset and a filing cabinet for the scarce one.
                </p>
                <PullQuote>
                  Tokens compound what your people knew. Development determines whether they will learn anything tomorrow worth encoding.
                </PullQuote>
                <p>
                  The organizations that thrive through this transition will run both loops. And the loops are made to feed each other. Your best people's encoded judgment can become the most patient sparring partner your rising generation has ever had: the master machinist available to every apprentice at midnight, the senior underwriter's pattern recognition turned into a mirror that asks the junior analyst better questions. Extraction and development were never enemies. A company that runs both loops well will develop people faster than any apprenticeship in history.
                </p>
                <p>
                  Know which loop is upstream. Tokens compound what your people knew. Development determines whether they will learn anything tomorrow worth encoding. Run only the first loop and you are drawing down a reservoir while telling yourself it's a renewable resource. The model gets smarter based on yesterday's judgment. The question is whether anyone in the building is growing tomorrow's.
                </p>

                <h2>The asset no one can extract</h2>
                <p>
                  Which brings us to the thing the balance sheet has never known how to hold.
                </p>
                <p>
                  Neither human capital nor token capital is property. People leave. Weights sit in a vendor's stack. What an organization can own, fully and untouchably, is conditions: the practices, the culture, the quality of attention and relationship that grow judgment worth encoding and make extraordinary people want to stay and become more extraordinary. Conditions cannot be extracted. Conditions cannot leak. And conditions compound, because every person who deepens inside them raises the field for everyone who follows.
                </p>
                <p>
                  This is the real work of leadership in the token capital era, and it is older than this era. Build the conditions in which human beings develop. Let the machines hold what your people have known. Make sure the people are becoming someone new.
                </p>
                <p>
                  Human capital and token capital are both balance sheet language, and balance sheets have their place. But the real asset has a more accurate and honoring name: people, becoming.
                </p>
                <p>
                  The Second Loop is where that becoming happens. In the essays that follow, I will explore what running it looks like: what develops judgment, what grows trust, what an organization measures when it takes the scarce asset as seriously as the abundant one. The token loop has thousands of engineers building it right now.
                </p>
                <p>
                  The second loop is waiting for its builders.
                </p>
              </div>

              {/* Author block */}
              <aside className="mt-20 pt-10 border-t border-border">
                <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-4">
                  About the author
                </p>
                <p className="text-foreground leading-relaxed">
                  Kent Frazier is the founder of ParadoxEdge. A former HR executive and Certified Integral Master Coach, he has spent three decades inside the work of leadership, partnering with senior leaders to develop the inner capacities and outer capabilities that meet the moment.
                </p>
                <div className="mt-8">
                  <a
                    href="https://calendly.com/kentfrazier/30-minutes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 py-3 text-sm font-semibold rounded-full transition-all duration-300"
                  >
                    Begin a Conversation
                  </a>
                </div>
              </aside>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default TheSecondLoop;